import { IconSparkles } from "@tabler/icons-react"
import { type FC, useEffect, useState } from "react"

import { aiGenerationCreditCosts } from "@forento/core/models/ai"
import { type PlatformLanguageId, platformLanguageIds, platformLanguages } from "@forento/core/models/platform"
import { type StudentEmailEditorDocument, type StudentEmailId } from "@forento/core/models/studentEmail"
import { toNumberFormat } from "@forento/core/utilities/number"

import AiGenerationDiscountNotice from "~/components/AiGenerationDiscountNotice"
import AiImagePicker from "~/components/AiImagePicker"
import Button, { SubmitButton } from "~/components/Button"
import Dropdown from "~/components/Dropdown"
import { InputTextArea } from "~/components/InputField"
import { DefaultModal, ModalButtons, ModalInputs, ModalText, ModalTitle } from "~/components/Modal"
import { uploadImage } from "~/components/StudentEmailEditor"
import { useAlert } from "~/contexts/AlertContext"
import { usePlatform, useUser } from "~/contexts/UserContext"
import { captureForentoAiEvent, getBuyAiCreditsUrl, useForentoAiModalTracking } from "~/utilities/forentoAiAnalytics"
import { query } from "~/utilities/trpc"

const languageItems = platformLanguageIds.map(id => ({ id, title: platformLanguages[id].label }))

const AI_INPUT_LIMITS = { description: 1_000, images: 4 } as const

export type AiStudentEmailTarget =
	| { type: "standard"; id: StudentEmailId }
	| { type: "product-access"; id: number }
	| { type: "lead-form"; formKey: string }

type Props = {
	isOpen: boolean
	email: AiStudentEmailTarget
	onClose(): void
	onGenerated(result: { title: string; document: StudentEmailEditorDocument; generationId: number }): void
}
const AiStudentEmailModal: FC<Props> = ({ isOpen, email, onClose, onGenerated }) => {
	const alert = useAlert()
	const user = useUser()
	const platform = usePlatform()!
	const inputLimits = user.user?.isImpersonating ? undefined : AI_INPUT_LIMITS

	const [description, setDescription] = useState("")
	const [images, setImages] = useState<File[]>([])
	const [isUploading, setIsUploading] = useState(false)
	const [language, setLanguage] = useState<PlatformLanguageId>(platform.language)

	const generate = query.ai.generateStudentEmail.useMutation({
		async onSuccess(result) {
			captureForentoAiEvent("forento_ai_generation_succeeded", { type: "student-email" })
			onGenerated(result)
			await user.reload()
			reset()
			onClose()
		},
		async onError(error) {
			console.error(error)
			captureForentoAiEvent("forento_ai_generation_failed", { type: "student-email" })
			await alert.show("Error", "Failed to generate email. Please try again.")
		},
	})

	useEffect(() => {
		if (isOpen) setLanguage(platform.language)
	}, [isOpen, platform.language])

	const generationCost = aiGenerationCreditCosts["student-email"]
	const hasEnoughCredits = platform.aiCreditBalance >= generationCost
	useForentoAiModalTracking({
		type: "student-email",
		isFormVisible: isOpen,
		creditBalance: platform.aiCreditBalance,
		cost: generationCost,
	})
	const isGenerating = isUploading || generate.isPending
	const canGenerate = description.trim().length > 0 && hasEnoughCredits && !isGenerating

	const handleGenerate = async () => {
		if (!canGenerate) return
		captureForentoAiEvent("forento_ai_generation_submitted", { type: "student-email" })

		const imageUrls: string[] = []
		setIsUploading(true)
		try {
			for (const file of images) {
				imageUrls.push((await uploadImage(file)).url)
			}
		} catch (error) {
			console.error(error)
			await alert.show("Error", "Failed to upload the images. Please try again.")
			setIsUploading(false)
			return
		}
		setIsUploading(false)

		generate.mutate({ email, description, imageUrls, language })
	}

	const reset = () => {
		setDescription("")
		setImages([])
		setLanguage(platform.language)
	}

	return (
		<DefaultModal
			width={640}
			isOpen={isOpen}
			onSubmit={handleGenerate}
			onClose={
				isGenerating
					? undefined
					: () => {
							reset()
							onClose()
						}
			}
		>
			<ModalTitle>Write this email with Forento AI</ModalTitle>
			<ModalText>
				Describe what the email should say. You get a draft in the editor with your academy's branding, and
				nothing is saved until you do.
			</ModalText>
			<ModalInputs>
				<div className="grid gap-3 rounded-lg border border-gray-200 bg-white p-3 sm:grid-cols-3">
					<div>
						<div className="text-xs text-gray-500">Available balance</div>
						<div className="text-lg font-semibold text-gray-900">
							{toNumberFormat(platform.aiCreditBalance)} credits
						</div>
					</div>
					<div>
						<div className="text-xs text-gray-500">This generation costs</div>
						<div className="text-lg font-semibold text-gray-900">
							{toNumberFormat(generationCost)} credits
						</div>
					</div>
					<div className="flex items-center sm:justify-end">
						<Button
							variant="secondary"
							onClick={getBuyAiCreditsUrl("student-email")}
							className="w-full sm:w-auto"
						>
							Purchase credits
						</Button>
					</div>
					<AiGenerationDiscountNotice className="sm:col-span-3" />
					{!hasEnoughCredits && (
						<p className="text-sm text-red-600 sm:col-span-3">
							Purchase more credits before generating an email.
						</p>
					)}
				</div>
				<InputTextArea
					label="What should the email say?"
					value={description}
					onChange={setDescription}
					placeholder="The purpose of the email, points to cover, and the tone you want. Mention anything specific to include, like what to do next."
					maxCharacters={inputLimits?.description}
				/>
				<AiImagePicker
					label="Images"
					description="Optional. Forento AI places them in the email where they support the copy."
					files={images}
					onChange={setImages}
					maxFiles={inputLimits?.images}
					multiple
				/>
				<Dropdown
					label="Language"
					items={languageItems}
					selectedItemId={language}
					onChange={id => setLanguage(id as PlatformLanguageId)}
				/>
			</ModalInputs>
			<ModalButtons>
				<Button
					variant="secondary"
					onClick={() => {
						reset()
						onClose()
					}}
					isDisabled={isGenerating}
				>
					Cancel
				</Button>
				<SubmitButton variant="primary" isDisabled={!canGenerate} isLoading={isGenerating}>
					<IconSparkles className="size-4" />
					Write email ({toNumberFormat(generationCost)} credits)
				</SubmitButton>
			</ModalButtons>
		</DefaultModal>
	)
}

export default AiStudentEmailModal
